import {
  disciplineMetadata,
  ageCategoryMetadata,
  weightCategoryMetadata,
} from "../custom-disciplines/category-metadata.js";
import { Prisma as PrismaRemote } from "@prisma-remote/client";

import { getAthletesByCurrentLiftWeight } from "../group-util.js";
import {
  type CompatibleRemoteAgeCategory as AgeCategory,
  type CompatibleRemoteDiscipline as Discipline,
  type CompatibleRemoteWeightCategory as WeightCategory,
  type LiftField,
  LiftStatus,
  type RefereeColorField,
} from "../types.js";
import type {
  AthleteData,
  GroupData,
  InjectedData,
  LeagueData,
  LevelData,
  MeetData,
  NextLiftData,
  OrganisationData,
  RecordData,
  RefereeLights,
} from "./types.js";

export type CompetitionPreviewSexProfile =
  | "men"
  | "women"
  | "menWomen"
  | "mixedMen"
  | "mixedWomen"
  | "mixed";

export type CompetitionPreviewAgeCategoryCode =
  | "SJR"
  | "JR"
  | "SNR"
  | "M1"
  | "M2"
  | "M3"
  | "M4";

export interface CompetitionPreviewOptions {
  sexProfile?: CompetitionPreviewSexProfile;
  groupAgeCategoryCodes?: [
    CompetitionPreviewAgeCategoryCode,
    CompetitionPreviewAgeCategoryCode,
  ];
}

export interface CompetitionPreviewFrame {
  index: number;
  label: string;
  data: InjectedData;
}

export interface CompetitionPreviewResult {
  frames: CompetitionPreviewFrame[];
  options: Required<CompetitionPreviewOptions>;
  metadata: {
    groupCount: number;
    athletesPerGroup: number;
    overturnFrameIndexes: number[];
    recordFrameIndexes: number[];
    hmAthleteId: string;
    hmDisqualifiedAthleteId: string;
    movementBombOutAthleteId: string;
    placementIfSuccessfulFrameIndexes: number[];
    deadliftThirdChangeScenarios: CompetitionPreviewDeadliftThirdChangeScenario[];
    withdrawalScenarios: CompetitionPreviewWithdrawalScenario[];
  };
}

export type CompetitionPreviewDeadliftChangeActor =
  | "current"
  | "next"
  | "other";

export interface CompetitionPreviewDeadliftThirdChangeScenario {
  frameIndex: number;
  groupId: string;
  occurrence: number;
  actor: CompetitionPreviewDeadliftChangeActor;
  orderChanged: boolean;
  changedAthleteId: string;
  beforeCurrentAthleteId: string | null;
  afterCurrentAthleteId: string | null;
  previousWeight: number;
  newWeight: number;
  minimumWeight: number;
  beforeOrderIds: string[];
  afterOrderIds: string[];
  label: string;
}

export type CompetitionPreviewWithdrawalStopAfter = "squat" | "bench";

export interface CompetitionPreviewWithdrawalScenario {
  frameIndex: number;
  athleteId: string;
  groupId: string;
  stopAfter: CompetitionPreviewWithdrawalStopAfter;
  manuallyInvalidatedFields: AttemptField[];
  label: string;
}

type Movement = "squat" | "bench" | "deadlift";
type AttemptNumber = 1 | 2 | 3;
type AttemptField = `${Movement}${AttemptNumber}`;
type StatusField = `${AttemptField}Status`;
type AutoField = `${AttemptField}Auto`;
type OverturnField = `${AttemptField}Overturn`;
type OverturnColorField = `${AttemptField}OverturnColor`;

interface ScenarioState {
  athletes: AthleteData[];
  groups: GroupData[];
  currentGroup: GroupData | null;
  currentAthlete: AthleteData | null;
  currentMovement: Movement | null;
  attemptNumber: AttemptNumber | null;
  placementIfSuccessful: number | null;
  refereeLights: RefereeLights[] | null;
  nextLiftData: NextLiftData | null;
  timerSeconds: number;
  frames: CompetitionPreviewFrame[];
  records: RecordData[];
  levels: LevelData[];
  ageCategories: AgeCategory[];
  weightCategories: WeightCategory[];
  disciplines: Discipline[];
  leagues: LeagueData[];
  organisation: OrganisationData;
  currentMeet: MeetData;
  metadata: CompetitionPreviewResult["metadata"];
  deadliftThirdChangeScenarioCursorByGroup: Record<string, number>;
  lastJudgedDeadliftThirdWeightByGroup: Record<string, number>;
}

const FIXED_MEET_ID = "competition-preview-meet";
const FIXED_ORG_ID = "competition-preview-org";
const ATHLETES_PER_GROUP = 16;
const MOVEMENTS: Movement[] = ["squat", "bench", "deadlift"];
const ATTEMPTS: AttemptNumber[] = [1, 2, 3];
const ENTRY_FIELDS = [
  "licence",
  "club",
  "sex",
  "birthDate",
  "lastname",
  "firstname",
  "discipline",
] as const;
const WEIGH_IN_FIELDS = [
  "bodyweight",
  "lot",
  "squat1",
  "bench1",
  "deadlift1",
] as const;
const HEIGHT_FIELDS = [
  "squatHeight",
  "rackPosition",
  "benchHeight",
  "secuHeight",
  "cales",
] as const;

const DEFAULT_OPTIONS: Required<CompetitionPreviewOptions> = {
  sexProfile: "mixed",
  groupAgeCategoryCodes: ["SNR", "SNR"],
};

const DEADLIFT_THIRD_CHANGE_CASES: Array<{
  actor: CompetitionPreviewDeadliftChangeActor;
  orderChanged: boolean;
}> = [
  { actor: "current", orderChanged: false },
  { actor: "current", orderChanged: true },
  { actor: "next", orderChanged: false },
  { actor: "next", orderChanged: true },
  { actor: "other", orderChanged: false },
  { actor: "other", orderChanged: true },
];

const WITHDRAWAL_SCENARIOS: Array<{
  athleteId: string;
  stopAfter: CompetitionPreviewWithdrawalStopAfter;
}> = [
  { athleteId: "preview-athlete-1-7", stopAfter: "squat" },
  { athleteId: "preview-athlete-1-12", stopAfter: "squat" },
  { athleteId: "preview-athlete-2-1", stopAfter: "bench" },
  { athleteId: "preview-athlete-2-6", stopAfter: "bench" },
];

export function createLayoutCompetitionPreviewFrames(
  options: CompetitionPreviewOptions = {},
): CompetitionPreviewResult {
  const normalizedOptions: Required<CompetitionPreviewOptions> = {
    sexProfile: options.sexProfile ?? DEFAULT_OPTIONS.sexProfile,
    groupAgeCategoryCodes:
      options.groupAgeCategoryCodes ?? DEFAULT_OPTIONS.groupAgeCategoryCodes,
  };
  const disciplines = buildDisciplines();
  const ageCategories = buildAgeCategories();
  const weightCategories = buildWeightCategories();
  const leagues = buildLeagues();
  const groups = buildGroups();
  const templates = buildAthleteTemplates({
    ageCategories,
    disciplines,
    groups,
    options: normalizedOptions,
    weightCategories,
  });
  const organisation = buildOrganisation();
  const records = buildRecords({
    ageCategories,
    discipline: disciplines[0]!,
    organisation,
    templates,
    weightCategories,
  });
  const levels = buildLevels({
    ageCategories,
    discipline: disciplines[0]!,
    weightCategories,
  });
  const state: ScenarioState = {
    athletes: templates.map((template) => createEmptyAthlete(template)),
    groups,
    currentGroup: null,
    currentAthlete: null,
    currentMovement: null,
    attemptNumber: null,
    placementIfSuccessful: null,
    refereeLights: buildRefereeLights([]),
    nextLiftData: null,
    timerSeconds: 60,
    frames: [],
    records,
    levels,
    ageCategories,
    weightCategories,
    disciplines,
    leagues,
    organisation,
    currentMeet: buildMeet(leagues[0]!),
    metadata: {
      groupCount: 2,
      athletesPerGroup: ATHLETES_PER_GROUP,
      overturnFrameIndexes: [],
      recordFrameIndexes: [],
      hmAthleteId: templates[ATHLETES_PER_GROUP * 2 - 1]?.id ?? "",
      hmDisqualifiedAthleteId: templates[ATHLETES_PER_GROUP * 2 - 1]?.id ?? "",
      movementBombOutAthleteId: templates[3]?.id ?? "",
      placementIfSuccessfulFrameIndexes: [],
      deadliftThirdChangeScenarios: [],
      withdrawalScenarios: [],
    },
    deadliftThirdChangeScenarioCursorByGroup: {},
    lastJudgedDeadliftThirdWeightByGroup: {},
  };

  pushFrame(state, "Départ du scénario");

  for (const group of groups) {
    revealGroupData(state, group.id, templates);
  }

  state.currentGroup = groups[0] ?? null;
  pushFrame(state, "Groupe 1 défini comme groupe courant");

  runCompetition(state, templates);
  recalculatePlacements(state.athletes);
  state.currentAthlete = null;
  state.currentMovement = null;
  state.attemptNumber = null;
  state.placementIfSuccessful = null;
  state.refereeLights = buildRefereeLights([]);
  state.nextLiftData = null;
  pushFrame(state, "Fin de la simulation");

  return {
    frames: state.frames,
    options: normalizedOptions,
    metadata: state.metadata,
  };
}

function revealGroupData(
  state: ScenarioState,
  groupId: string,
  templates: AthleteData[],
): void {
  const groupIndex = state.groups.findIndex((group) => group.id === groupId);
  const groupTemplates = templates.filter(
    (athlete) => athlete.group_id === groupId,
  );

  for (const field of ENTRY_FIELDS) {
    for (const template of groupTemplates) {
      updateAthlete(state, template.id, (athlete) => {
        switch (field) {
          case "discipline":
            athlete.discipline = template.discipline;
            athlete.disciplineId = template.disciplineId;
            break;
          default:
            (athlete as unknown as Record<string, unknown>)[field] =
              template[field];
            break;
        }
      });
      pushFrame(
        state,
        `Groupe ${groupIndex + 1}: saisie ${field} ${template.lot}`,
      );
    }
  }

  for (const field of WEIGH_IN_FIELDS) {
    for (const template of groupTemplates) {
      updateAthlete(state, template.id, (athlete) => {
        (athlete as unknown as Record<string, unknown>)[field] =
          template[field];
      });
      pushFrame(
        state,
        `Groupe ${groupIndex + 1}: saisie ${field} ${template.lot}`,
      );
    }
  }

  for (const field of HEIGHT_FIELDS) {
    for (const template of groupTemplates) {
      updateAthlete(state, template.id, (athlete) => {
        (athlete as unknown as Record<string, unknown>)[field] =
          template[field];
      });
      pushFrame(
        state,
        `Groupe ${groupIndex + 1}: saisie ${field} ${template.lot}`,
      );
    }
  }
}

function runCompetition(state: ScenarioState, templates: AthleteData[]): void {
  let judgedAttemptCount = 0;

  for (const movement of MOVEMENTS) {
    for (const group of state.groups) {
      state.currentGroup = group;
      pushFrame(state, `${group.name}: début ${movement}`);

      for (const attemptNumber of ATTEMPTS) {
        const field = `${movement}${attemptNumber}` as AttemptField;
        const statusField = `${field}Status` as StatusField;

        while (true) {
          const groupAthletes = state.athletes.filter(
            (athlete) => athlete.group_id === group.id,
          );
          const ordered = getAthletesByCurrentLiftWeight(
            groupAthletes as never,
            field as LiftField,
          ) as unknown as AthleteData[];
          const current = ordered.find(
            (athlete) =>
              athlete[field] != null &&
              athlete[statusField] === LiftStatus.ANNONCED,
          );
          if (!current) break;

          state.currentAthlete = current;
          state.currentMovement = movement;
          state.attemptNumber = attemptNumber;
          state.refereeLights = buildRefereeLights([]);
          state.placementIfSuccessful = computePlacementIfSuccessful(
            state.athletes,
            current,
            movement,
            attemptNumber,
          );
          if (state.placementIfSuccessful === 1) {
            state.metadata.placementIfSuccessfulFrameIndexes.push(
              state.frames.length,
            );
          }
          state.nextLiftData = {
            athlete: current,
            movement,
            attemptNumber,
          };
          pushFrame(
            state,
            `${group.name}: ${current.firstname} ${current.lastname} appelé ${field}`,
          );

          if (
            movement === "deadlift" &&
            attemptNumber === 3 &&
            applyNextDeadliftThirdChangeScenario(state, group, field)
          ) {
            continue;
          }

          const athleteToJudge = state.currentAthlete ?? current;
          const decision = getAttemptDecision({
            athlete: athleteToJudge,
            attemptNumber,
            field,
            judgedAttemptCount,
            movement,
          });
          judgedAttemptCount += 1;
          applyAttemptDecision(state, athleteToJudge.id, field, decision);
          const judgedAthlete = state.athletes.find(
            (athlete) => athlete.id === athleteToJudge.id,
          )!;
          if (field === "deadlift3") {
            state.lastJudgedDeadliftThirdWeightByGroup[group.id] = Number(
              judgedAthlete[field] ?? 0,
            );
          }
          state.currentAthlete = judgedAthlete;
          state.refereeLights = buildRefereeLights(
            [
              judgedAthlete[`${field}LeftColor` as RefereeColorField] ?? "",
              judgedAthlete[`${field}ChiefColor` as RefereeColorField] ?? "",
              judgedAthlete[`${field}RightColor` as RefereeColorField] ?? "",
            ],
            decision.overturn ? decision.overturnColor : "",
          );
          state.placementIfSuccessful = computePlacementIfSuccessful(
            state.athletes,
            judgedAthlete,
            movement,
            attemptNumber,
          );
          pushFrame(
            state,
            `${group.name}: ${judgedAthlete.firstname} ${judgedAthlete.lastname} ${field} ${decision.passed ? "validé" : "invalidé"}`,
          );
          if (decision.overturn) {
            state.metadata.overturnFrameIndexes.push(state.frames.length - 1);
          }
          if (isRecordLift(state.records, judgedAthlete, movement, field)) {
            state.metadata.recordFrameIndexes.push(state.frames.length - 1);
          }

          if (
            applyWithdrawalScenario(
              state,
              judgedAthlete,
              movement,
              attemptNumber,
            )
          ) {
            recalculatePlacements(state.athletes);
            continue;
          }

          if (attemptNumber < 3) {
            const nextField = `${movement}${attemptNumber + 1}` as AttemptField;
            const nextWeight = decision.passed
              ? roundToStep(Number(judgedAthlete[field]) + 2.5)
              : Number(judgedAthlete[field]);
            updateAthlete(state, judgedAthlete.id, (athlete) => {
              athlete[nextField] = decimal(nextWeight);
              athlete[`${nextField}Status` as StatusField] =
                LiftStatus.ANNONCED;
            });
            state.currentAthlete =
              state.athletes.find(
                (athlete) => athlete.id === judgedAthlete.id,
              ) ?? null;
            state.nextLiftData = state.currentAthlete
              ? {
                  athlete: state.currentAthlete,
                  movement,
                  attemptNumber: (attemptNumber + 1) as AttemptNumber,
                }
              : null;
            pushFrame(
              state,
              `${group.name}: saisie ${nextField} ${nextWeight} kg`,
            );
          }

          recalculatePlacements(state.athletes);
        }
      }
    }
  }

  for (const template of templates) {
    const athlete = state.athletes.find((entry) => entry.id === template.id);
    if (!athlete) continue;
    athlete.totalAnnonced = decimal(Number(athlete.totalkg ?? 0));
  }
}

function applyWithdrawalScenario(
  state: ScenarioState,
  athlete: AthleteData,
  movement: Movement,
  attemptNumber: AttemptNumber,
): boolean {
  if (
    (movement !== "squat" && movement !== "bench") ||
    attemptNumber !== 3 ||
    state.metadata.withdrawalScenarios.some(
      (scenario) => scenario.athleteId === athlete.id,
    )
  ) {
    return false;
  }

  const scenario = WITHDRAWAL_SCENARIOS.find(
    (entry) => entry.athleteId === athlete.id && entry.stopAfter === movement,
  );
  if (!scenario) return false;

  const manuallyInvalidatedFields = getWithdrawalFields(scenario.stopAfter);
  updateAthlete(state, athlete.id, (entry) => {
    for (const field of manuallyInvalidatedFields) {
      entry[`${field}Status` as StatusField] = LiftStatus.NO_LIFT;
      entry[`${field}Auto` as AutoField] = false;
      entry[`${field}Overturn` as OverturnField] = false;
      entry[`${field}OverturnColor` as OverturnColorField] = "";
      entry[`${field}LeftColor` as RefereeColorField] = "";
      entry[`${field}ChiefColor` as RefereeColorField] = "";
      entry[`${field}RightColor` as RefereeColorField] = "";
    }
    entry.totalkg = decimal(computeSuccessfulTotal(entry));
    entry.totalAnnonced = decimal(computeAnnouncedTotal(entry));
  });

  const updatedAthlete = state.athletes.find(
    (entry) => entry.id === athlete.id,
  )!;
  const label = `${state.currentGroup?.name ?? "Groupe"}: abandon ${updatedAthlete.firstname} ${updatedAthlete.lastname} après ${scenario.stopAfter}`;
  state.currentAthlete = updatedAthlete;
  state.refereeLights = buildRefereeLights([]);
  state.nextLiftData = null;
  state.placementIfSuccessful = null;
  state.metadata.withdrawalScenarios.push({
    frameIndex: state.frames.length,
    athleteId: updatedAthlete.id,
    groupId: updatedAthlete.group_id,
    stopAfter: scenario.stopAfter,
    manuallyInvalidatedFields,
    label,
  });
  pushFrame(state, label);
  return true;
}

function getWithdrawalFields(
  stopAfter: CompetitionPreviewWithdrawalStopAfter,
): AttemptField[] {
  if (stopAfter === "squat") {
    return [
      "bench1",
      "bench2",
      "bench3",
      "deadlift1",
      "deadlift2",
      "deadlift3",
    ];
  }
  return ["deadlift1", "deadlift2", "deadlift3"];
}

function applyNextDeadliftThirdChangeScenario(
  state: ScenarioState,
  group: GroupData,
  field: AttemptField,
): boolean {
  const cursor = state.deadliftThirdChangeScenarioCursorByGroup[group.id] ?? 0;
  const scenario = DEADLIFT_THIRD_CHANGE_CASES[cursor];
  if (!scenario) return false;

  const beforeOrder = getOrderedAnnouncedAthletes(state, group.id, field);
  const actorIndex = getDeadliftChangeActorIndex(scenario.actor, beforeOrder);
  const actor = beforeOrder[actorIndex];
  const beforeCurrent = beforeOrder[0] ?? null;
  if (!actor || !beforeCurrent) return false;

  const minimumWeight =
    state.lastJudgedDeadliftThirdWeightByGroup[group.id] ?? 0;
  const newWeight = getDeadliftThirdChangeWeight({
    actorIndex,
    beforeOrder,
    minimumWeight,
    orderChanged: scenario.orderChanged,
  });
  const previousWeight = Number(actor[field] ?? 0);
  if (newWeight === previousWeight) return false;

  updateAthlete(state, actor.id, (athlete) => {
    athlete[field] = decimal(newWeight);
    athlete.totalAnnonced = decimal(computeAnnouncedTotal(athlete));
  });

  const afterOrder = getOrderedAnnouncedAthletes(state, group.id, field);
  const afterCurrent = afterOrder[0] ?? null;
  state.currentAthlete = afterCurrent;
  state.nextLiftData = afterCurrent
    ? {
        athlete: afterCurrent,
        movement: "deadlift",
        attemptNumber: 3,
      }
    : null;
  state.placementIfSuccessful = afterCurrent
    ? computePlacementIfSuccessful(state.athletes, afterCurrent, "deadlift", 3)
    : null;

  const actualOrderChanged = hasOrderChanged(beforeOrder, afterOrder);
  const metadata: CompetitionPreviewDeadliftThirdChangeScenario = {
    frameIndex: state.frames.length,
    groupId: group.id,
    occurrence: cursor + 1,
    actor: scenario.actor,
    orderChanged: actualOrderChanged,
    changedAthleteId: actor.id,
    beforeCurrentAthleteId: beforeCurrent.id,
    afterCurrentAthleteId: afterCurrent?.id ?? null,
    previousWeight,
    newWeight,
    minimumWeight,
    beforeOrderIds: beforeOrder.map((athlete) => athlete.id),
    afterOrderIds: afterOrder.map((athlete) => athlete.id),
    label: buildDeadliftThirdChangeLabel(group, scenario, cursor + 1),
  };
  state.metadata.deadliftThirdChangeScenarios.push(metadata);
  state.deadliftThirdChangeScenarioCursorByGroup[group.id] = cursor + 1;
  pushFrame(state, metadata.label);

  return scenario.actor === "current" && beforeCurrent.id !== afterCurrent?.id;
}

function getOrderedAnnouncedAthletes(
  state: ScenarioState,
  groupId: string,
  field: AttemptField,
): AthleteData[] {
  const statusField = `${field}Status` as StatusField;
  return (
    getAthletesByCurrentLiftWeight(
      state.athletes.filter((athlete) => athlete.group_id === groupId) as never,
      field as LiftField,
    ) as unknown as AthleteData[]
  ).filter(
    (athlete) =>
      athlete[field] != null && athlete[statusField] === LiftStatus.ANNONCED,
  );
}

function getDeadliftChangeActorIndex(
  actor: CompetitionPreviewDeadliftChangeActor,
  beforeOrder: AthleteData[],
): number {
  switch (actor) {
    case "current":
      return 0;
    case "next":
      return Math.min(1, beforeOrder.length - 1);
    case "other":
      return Math.min(3, beforeOrder.length - 1);
  }
}

function getDeadliftThirdChangeWeight({
  actorIndex,
  beforeOrder,
  minimumWeight,
  orderChanged,
}: {
  actorIndex: number;
  beforeOrder: AthleteData[];
  minimumWeight: number;
  orderChanged: boolean;
}): number {
  const actor = beforeOrder[actorIndex]!;
  const previousWeight = Number(actor.deadlift3 ?? 0);
  const minWeight = roundToStep(Math.max(minimumWeight, 20));
  const weightAt = (index: number) =>
    Number(beforeOrder[index]?.deadlift3 ?? 0);
  let candidate = previousWeight;

  if (actorIndex === 0) {
    const nextWeight = weightAt(1);
    candidate = orderChanged
      ? nextWeight + 2.5
      : Math.min(nextWeight, previousWeight + 2.5);
  } else if (actorIndex === 1) {
    const nextWeight = weightAt(2);
    candidate = orderChanged
      ? nextWeight + 2.5
      : Math.min(nextWeight - 2.5, previousWeight + 2.5);
  } else {
    const previousOrderWeight = weightAt(actorIndex - 1);
    const nextWeight = weightAt(actorIndex + 1);
    candidate = orderChanged
      ? previousOrderWeight - 2.5
      : Math.min(
          nextWeight > 0 ? nextWeight - 2.5 : previousWeight + 2.5,
          previousWeight + 2.5,
        );
  }

  candidate = roundToStep(Math.max(candidate, minWeight));
  if (candidate === previousWeight) {
    candidate = roundToStep(previousWeight + 2.5);
  }
  return candidate;
}

function hasOrderChanged(
  beforeOrder: AthleteData[],
  afterOrder: AthleteData[],
): boolean {
  return (
    beforeOrder.map((athlete) => athlete.id).join("|") !==
    afterOrder.map((athlete) => athlete.id).join("|")
  );
}

function buildDeadliftThirdChangeLabel(
  group: GroupData,
  scenario: {
    actor: CompetitionPreviewDeadliftChangeActor;
    orderChanged: boolean;
  },
  occurrence: number,
): string {
  const actorLabel =
    scenario.actor === "current"
      ? "current"
      : scenario.actor === "next"
        ? "next"
        : "other";
  const orderLabel = scenario.orderChanged ? "ordre changé" : "ordre inchangé";
  return `${group.name}: changement deadlift3 ${actorLabel} ${orderLabel} #${occurrence}`;
}

function applyAttemptDecision(
  state: ScenarioState,
  athleteId: string,
  field: AttemptField,
  decision: {
    passed: boolean;
    colors: [string, string, string];
    overturn: boolean;
    overturnColor: string;
  },
): void {
  updateAthlete(state, athleteId, (athlete) => {
    athlete[`${field}Status` as StatusField] = decision.passed
      ? LiftStatus.LIFT
      : LiftStatus.NO_LIFT;
    athlete[`${field}Auto` as AutoField] = true;
    athlete[`${field}Overturn` as OverturnField] = decision.overturn;
    athlete[`${field}OverturnColor` as OverturnColorField] =
      decision.overturnColor;
    athlete[`${field}LeftColor` as RefereeColorField] = decision.colors[0];
    athlete[`${field}ChiefColor` as RefereeColorField] = decision.colors[1];
    athlete[`${field}RightColor` as RefereeColorField] = decision.colors[2];
    athlete.totalkg = decimal(computeSuccessfulTotal(athlete));
    athlete.totalAnnonced = decimal(computeAnnouncedTotal(athlete));
  });
}

function getAttemptDecision({
  athlete,
  attemptNumber,
  field,
  judgedAttemptCount,
  movement,
}: {
  athlete: AthleteData;
  attemptNumber: AttemptNumber;
  field: AttemptField;
  judgedAttemptCount: number;
  movement: Movement;
}): {
  passed: boolean;
  colors: [string, string, string];
  overturn: boolean;
  overturnColor: string;
} {
  const isMovementBombOut =
    athlete.lot === 4 &&
    movement === "squat" &&
    athlete.group_id.endsWith("-1");
  const basePassed =
    !isMovementBombOut &&
    (athlete.lot + attemptNumber + movement.length) % 5 !== 0;
  const overturnSlots = new Set([
    7, 15, 19, 31, 39, 43, 55, 63, 67, 79, 87, 91,
  ]);
  const shouldOverturn = overturnSlots.has(judgedAttemptCount);

  if (shouldOverturn) {
    if (judgedAttemptCount % 2 === 0) {
      return {
        passed: true,
        colors: ["white", "red", "red"],
        overturn: true,
        overturnColor: "white",
      };
    }
    return {
      passed: false,
      colors: ["white", "white", "red"],
      overturn: true,
      overturnColor: "red",
    };
  }

  if (!basePassed) {
    return {
      passed: false,
      colors: ["red", field.startsWith("bench") ? "blue" : "red", "white"],
      overturn: false,
      overturnColor: "",
    };
  }

  return {
    passed: true,
    colors: [
      "white",
      "white",
      (athlete.lot + attemptNumber) % 3 === 0 ? "red" : "white",
    ],
    overturn: false,
    overturnColor: "",
  };
}

function computePlacementIfSuccessful(
  athletes: AthleteData[],
  currentAthlete: AthleteData,
  movement: Movement,
  attemptNumber: AttemptNumber,
): number | null {
  const field = `${movement}${attemptNumber}` as AttemptField;
  const attemptedWeight = Number(currentAthlete[field] ?? 0);
  if (!attemptedWeight) return null;
  if (
    currentAthlete.id === "preview-athlete-1-3" &&
    movement === "deadlift" &&
    attemptNumber === 3
  ) {
    return 1;
  }

  const projectedTotal =
    getBestTotalBeforeAttempt(currentAthlete, movement, attemptNumber) +
    attemptedWeight;
  const categoryAthletes = athletes.filter(
    (athlete) =>
      athlete.id !== currentAthlete.id &&
      athlete.sex === currentAthlete.sex &&
      athlete.ageCategoryId === currentAthlete.ageCategoryId &&
      athlete.weightCategoryId === currentAthlete.weightCategoryId &&
      !athlete.isDisqualified,
  );
  const betterTotals = categoryAthletes.filter(
    (athlete) => Number(athlete.totalkg ?? 0) >= projectedTotal,
  );
  return betterTotals.length + 1;
}

function computeSuccessfulTotal(athlete: AthleteData): number {
  return MOVEMENTS.reduce((total, movement) => {
    let best = 0;
    for (const attemptNumber of ATTEMPTS) {
      const field = `${movement}${attemptNumber}` as AttemptField;
      const status = athlete[`${field}Status` as StatusField];
      if (status === LiftStatus.LIFT) {
        best = Math.max(best, Number(athlete[field] ?? 0));
      }
    }
    return total + best;
  }, 0);
}

function computeAnnouncedTotal(athlete: AthleteData): number {
  return MOVEMENTS.reduce((total, movement) => {
    let best = 0;
    let announced = 0;
    for (const attemptNumber of ATTEMPTS) {
      const field = `${movement}${attemptNumber}` as AttemptField;
      const status = athlete[`${field}Status` as StatusField];
      if (status === LiftStatus.LIFT) {
        best = Math.max(best, Number(athlete[field] ?? 0));
      }
      if (status === LiftStatus.ANNONCED) {
        announced = Math.max(announced, Number(athlete[field] ?? 0));
      }
    }
    return total + Math.max(best, announced);
  }, 0);
}

function getBestTotalBeforeAttempt(
  athlete: AthleteData,
  movement: Movement,
  attemptNumber: AttemptNumber,
): number {
  return MOVEMENTS.reduce((total, currentMovement) => {
    const maxAttempt =
      currentMovement === movement ? Math.max(0, attemptNumber - 1) : 3;
    let best = 0;
    for (let attempt = 1; attempt <= maxAttempt; attempt += 1) {
      const field = `${currentMovement}${attempt}` as AttemptField;
      const status = athlete[`${field}Status` as StatusField];
      if (status === LiftStatus.LIFT) {
        best = Math.max(best, Number(athlete[field] ?? 0));
      }
    }
    return total + best;
  }, 0);
}

function isRecordLift(
  records: RecordData[],
  athlete: AthleteData,
  movement: Movement,
  field: AttemptField,
): boolean {
  if (athlete[`${field}Status` as StatusField] !== LiftStatus.LIFT)
    return false;
  const matchingRecord = records.find(
    (record) =>
      record.movement === movement &&
      record.sex === athlete.sex &&
      record.ageCategoryId === athlete.ageCategoryId &&
      record.weightCategoryId === athlete.weightCategoryId,
  );
  return matchingRecord
    ? Number(athlete[field] ?? 0) > matchingRecord.weight
    : false;
}

function recalculatePlacements(athletes: AthleteData[]): void {
  const categoryKeys = new Set(
    athletes.map(
      (athlete) =>
        `${athlete.sex}:${athlete.ageCategoryId}:${athlete.weightCategoryId}`,
    ),
  );
  for (const key of categoryKeys) {
    const categoryAthletes = athletes
      .filter(
        (athlete) =>
          `${athlete.sex}:${athlete.ageCategoryId}:${athlete.weightCategoryId}` ===
          key,
      )
      .sort((left, right) => {
        const totalDelta =
          Number(right.totalkg ?? 0) - Number(left.totalkg ?? 0);
        if (totalDelta !== 0) return totalDelta;
        return Number(left.bodyweight ?? 0) - Number(right.bodyweight ?? 0);
      });
    categoryAthletes.forEach((athlete, index) => {
      athlete.rank = athlete.isDisqualified ? null : index + 1;
    });
  }
}

function updateAthlete(
  state: ScenarioState,
  athleteId: string,
  update: (athlete: AthleteData) => void,
): void {
  const athlete = state.athletes.find((entry) => entry.id === athleteId);
  if (!athlete) return;
  update(athlete);
}

function pushFrame(state: ScenarioState, label: string): void {
  const timerSeconds = Math.max(0, state.timerSeconds);
  const data: InjectedData = {
    currentAthlete: state.currentAthlete
      ? cloneAthlete(state.currentAthlete)
      : null,
    athletes: state.athletes.map(cloneAthlete),
    currentGroup: state.currentGroup ? { ...state.currentGroup } : null,
    groups: state.groups.map((group) => ({ ...group })),
    currentMeet: state.currentMeet,
    timer: formatTimer(timerSeconds),
    timerSeconds,
    attemptTimers: [formatTimer(timerSeconds)],
    attemptTimerSeconds: [timerSeconds],
    currentMovement: state.currentMovement,
    attemptNumber: state.attemptNumber ?? undefined,
    placementIfSuccessful: state.placementIfSuccessful,
    refereeLights: state.refereeLights
      ? state.refereeLights.map((light) => ({ ...light }))
      : null,
    nextLiftData: state.nextLiftData
      ? {
          athlete: cloneAthlete(state.nextLiftData.athlete),
          movement: state.nextLiftData.movement,
          attemptNumber: state.nextLiftData.attemptNumber,
        }
      : null,
    records: state.records,
    levels: state.levels,
    ageCategories: state.ageCategories,
    weightCategories: state.weightCategories,
    disciplines: state.disciplines,
    leagues: state.leagues,
    organisation: state.organisation,
    themeColors: {
      primary: "#0056B3",
      primaryText: "#ffffff",
      background: "#ffffff",
      backgroundText: "#000000",
      rankingText: "#000000",
      ranking: "#ffffff",
    },
  };
  state.frames.push({ index: state.frames.length, label, data });
  state.timerSeconds = timerSeconds === 0 ? 60 : timerSeconds - 5;
}

function cloneAthlete(athlete: AthleteData): AthleteData {
  return { ...athlete };
}

function buildAthleteTemplates({
  ageCategories,
  disciplines,
  groups,
  options,
  weightCategories,
}: {
  ageCategories: AgeCategory[];
  disciplines: Discipline[];
  groups: GroupData[];
  options: Required<CompetitionPreviewOptions>;
  weightCategories: WeightCategory[];
}): AthleteData[] {
  const discipline = disciplines[0]!;
  const templates: AthleteData[] = [];

  for (const [groupIndex, group] of groups.entries()) {
    const sexes = buildGroupSexes(options.sexProfile, groupIndex);
    const ageCategory =
      ageCategories.find(
        (category) =>
          category.code === options.groupAgeCategoryCodes[groupIndex],
      ) ?? ageCategories[2]!;

    for (
      let athleteIndex = 0;
      athleteIndex < ATHLETES_PER_GROUP;
      athleteIndex += 1
    ) {
      const sex = sexes[athleteIndex]!;
      const categoriesForSex = weightCategories.filter(
        (category) => category.sex === sex,
      );
      const weightCategory =
        categoriesForSex[athleteIndex % categoriesForSex.length] ??
        weightCategories[0]!;
      const bodyweight = Math.max(
        weightCategory.minWeight + 1,
        Math.min(
          weightCategory.maxWeight - 0.5,
          weightCategory.minWeight + 3.5,
        ),
      );
      const lot = groupIndex * ATHLETES_PER_GROUP + athleteIndex + 1;
      const base = sex === "F" ? 65 : 105;
      const squat1 = roundToStep(base + athleteIndex * 5 + groupIndex * 7.5);
      const bench1 = roundToStep(
        base * 0.65 + athleteIndex * 2.5 + groupIndex * 5,
      );
      const deadlift1 = roundToStep(
        base * 1.25 + athleteIndex * 5 + groupIndex * 7.5,
      );

      templates.push(
        createFullAthlete({
          id: `preview-athlete-${groupIndex + 1}-${athleteIndex + 1}`,
          group,
          firstname:
            PREVIEW_FIRSTNAMES[athleteIndex % PREVIEW_FIRSTNAMES.length]!,
          lastname: `${sex === "F" ? "Femme" : "Homme"} ${athleteIndex + 1}`,
          club: `Club ${groupIndex + 1}`,
          sex,
          birthDate: buildBirthDate(ageCategory),
          bodyweight,
          lot,
          squat1: athleteIndex === 0 || athleteIndex === 1 ? 100 : squat1,
          bench1,
          deadlift1,
          discipline,
          ageCategory,
          weightCategory,
          isDisqualified: groupIndex === 1 && athleteIndex === 15,
        }),
      );
    }
  }

  return templates;
}

function createEmptyAthlete(template: AthleteData): AthleteData {
  return {
    ...template,
    lastname: "",
    firstname: "",
    club: "",
    licence: "",
    sex: "",
    birthDate: template.birthDate,
    bodyweight: null,
    lot: template.lot,
    squat1: null,
    squat2: null,
    squat3: null,
    bench1: null,
    bench2: null,
    bench3: null,
    deadlift1: null,
    deadlift2: null,
    deadlift3: null,
    totalkg: decimal(0),
    totalAnnonced: decimal(0),
    rank: null,
    discipline: template.discipline,
  };
}

function createFullAthlete({
  id,
  group,
  firstname,
  lastname,
  club,
  sex,
  birthDate,
  bodyweight,
  lot,
  squat1,
  bench1,
  deadlift1,
  discipline,
  ageCategory,
  weightCategory,
  isDisqualified,
}: {
  id: string;
  group: GroupData;
  firstname: string;
  lastname: string;
  club: string;
  sex: string;
  birthDate: Date;
  bodyweight: number;
  lot: number;
  squat1: number;
  bench1: number;
  deadlift1: number;
  discipline: Discipline;
  ageCategory: AgeCategory;
  weightCategory: WeightCategory;
  isDisqualified: boolean;
}): AthleteData {
  return {
    id,
    lastname,
    firstname,
    club,
    licence: `PREVIEW-${lot.toString().padStart(3, "0")}`,
    sex,
    birthDate,
    bodyweight,
    lot,
    group_id: group.id,
    isDisqualified,
    squat1: decimal(squat1),
    squat1Status: LiftStatus.ANNONCED,
    squat1Auto: false,
    squat1Overturn: false,
    squat1LeftColor: "",
    squat1ChiefColor: "",
    squat1RightColor: "",
    squat1OverturnColor: "",
    squat2: null,
    squat2Status: LiftStatus.ANNONCED,
    squat2Auto: false,
    squat2Overturn: false,
    squat2LeftColor: "",
    squat2ChiefColor: "",
    squat2RightColor: "",
    squat2OverturnColor: "",
    squat3: null,
    squat3Status: LiftStatus.ANNONCED,
    squat3Auto: false,
    squat3Overturn: false,
    squat3LeftColor: "",
    squat3ChiefColor: "",
    squat3RightColor: "",
    squat3OverturnColor: "",
    bench1: decimal(bench1),
    bench1Status: LiftStatus.ANNONCED,
    bench1Auto: false,
    bench1Overturn: false,
    bench1LeftColor: "",
    bench1ChiefColor: "",
    bench1RightColor: "",
    bench1OverturnColor: "",
    bench2: null,
    bench2Status: LiftStatus.ANNONCED,
    bench2Auto: false,
    bench2Overturn: false,
    bench2LeftColor: "",
    bench2ChiefColor: "",
    bench2RightColor: "",
    bench2OverturnColor: "",
    bench3: null,
    bench3Status: LiftStatus.ANNONCED,
    bench3Auto: false,
    bench3Overturn: false,
    bench3LeftColor: "",
    bench3ChiefColor: "",
    bench3RightColor: "",
    bench3OverturnColor: "",
    deadlift1: decimal(deadlift1),
    deadlift1Status: LiftStatus.ANNONCED,
    deadlift1Auto: false,
    deadlift1Overturn: false,
    deadlift1LeftColor: "",
    deadlift1ChiefColor: "",
    deadlift1RightColor: "",
    deadlift1OverturnColor: "",
    deadlift2: null,
    deadlift2Status: LiftStatus.ANNONCED,
    deadlift2Auto: false,
    deadlift2Overturn: false,
    deadlift2LeftColor: "",
    deadlift2ChiefColor: "",
    deadlift2RightColor: "",
    deadlift2OverturnColor: "",
    deadlift3: null,
    deadlift3Status: LiftStatus.ANNONCED,
    deadlift3Auto: false,
    deadlift3Overturn: false,
    deadlift3LeftColor: "",
    deadlift3ChiefColor: "",
    deadlift3RightColor: "",
    deadlift3OverturnColor: "",
    totalkg: decimal(0),
    totalAnnonced: decimal(0),
    rank: null,
    squatHeight: 12 + (lot % 5),
    benchHeight: 8 + (lot % 4),
    secuHeight: 9 + (lot % 3),
    cales: lot % 2 === 0 ? 2 : 0,
    rackPosition: lot % 2 === 0 ? "INT" : "EXT",
    ageCategoryId: ageCategory.id,
    weightCategoryId: weightCategory.id,
    points: decimal(0),
    disciplineId: discipline.id,
    discipline,
    ageCategory,
    weightCategory,
    // Preview athletes are never linked to Powerscout
    powerscoutLifterId: null,
    powerscoutBests: {
      best_squat_kg: decimal(190),
      best_bench_kg: decimal(130),
      best_deadlift_kg: decimal(240),
      best_total_kg: decimal(560),
    } as NonNullable<AthleteData["powerscoutBests"]>,
  };
}

function buildGroups(): GroupData[] {
  return [1, 2].map<GroupData>((groupNumber) => ({
    id: `preview-group-${groupNumber}`,
    name: `Groupe ${groupNumber}`,
    meet_id: FIXED_MEET_ID,
    plateauNumber: 1,
    groupNumber,
    observations: null,
    secretary: "Secrétaire Preview",
    referee1: "Arbitre gauche",
    referee2: "Arbitre droit",
    centralReferee: "Arbitre central",
    jury: "Jury Preview",
    share: true,
    competitionFormat: "FULL_POWER",
    customDisciplineGroupId: null,
    selected: groupNumber === 1,
  }));
}

function buildMeet(ligue: LeagueData): MeetData {
  return {
    id: FIXED_MEET_ID,
    name: "Preview compétition",
    city: "Paris",
    startdate: new Date("2026-08-13"),
    enddate: new Date("2026-08-13"),
    created_at: new Date("2026-08-13"),
    ligue_id: ligue.id,
    organisationId: FIXED_ORG_ID,
    licenseKey: "PREVIEW",
    streamUrl: "",
    themeName: null,
    isPublic: false,
    isOver: false,
    ligue,
  };
}

function buildDisciplines(): Discipline[] {
  return [
    {
      ...disciplineMetadata({}),
      id: 1,
      name: "Force Athlétique",
      code: "FA",
      competitionFormat: "FULL_POWER",
    },
    {
      ...disciplineMetadata({}),
      id: 3,
      name: "Powerlifting",
      code: "PL",
      competitionFormat: "FULL_POWER",
    },
  ];
}

function buildAgeCategories(): AgeCategory[] {
  return [
    {
      ...ageCategoryMetadata({}),
      id: 1,
      name: "Sub-Junior",
      recordName: "Sub-Junior",
      code: "SJR",
      ageMin: 14,
      ageMax: 18,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 2,
      name: "Junior",
      recordName: "Junior",
      code: "JR",
      ageMin: 19,
      ageMax: 23,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 3,
      name: "Senior",
      recordName: "Senior",
      code: "SNR",
      ageMin: 24,
      ageMax: 39,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 4,
      name: "Master 1",
      recordName: "Master 1",
      code: "M1",
      ageMin: 40,
      ageMax: 49,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 5,
      name: "Master 2",
      recordName: "Master 2",
      code: "M2",
      ageMin: 50,
      ageMax: 59,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 6,
      name: "Master 3",
      recordName: "Master 3",
      code: "M3",
      ageMin: 60,
      ageMax: 69,
      disciplineId: 1,
    },
    {
      ...ageCategoryMetadata({}),
      id: 7,
      name: "Master 4",
      recordName: "Master 4",
      code: "M4",
      ageMin: 70,
      ageMax: 99,
      disciplineId: 1,
    },
  ];
}

function buildWeightCategories(): WeightCategory[] {
  const men = [
    ["59 Kg", 0, 59],
    ["66 Kg", 59, 66],
    ["74 Kg", 66, 74],
    ["83 Kg", 74, 83],
    ["93 Kg", 83, 93],
    ["105 Kg", 93, 105],
    ["120 Kg", 105, 120],
    ["+120 Kg", 120, 999],
  ] as const;
  const women = [
    ["47 Kg", 0, 47],
    ["52 Kg", 47, 52],
    ["57 Kg", 52, 57],
    ["63 Kg", 57, 63],
    ["69 Kg", 63, 69],
    ["76 Kg", 69, 76],
    ["84 Kg", 76, 84],
    ["+84 Kg", 84, 999],
  ] as const;
  return [
    ...men.map((entry, index) => buildWeightCategory(index + 1, entry, "M")),
    ...women.map((entry, index) => buildWeightCategory(index + 11, entry, "F")),
  ];
}

function buildWeightCategory(
  id: number,
  [name, minWeight, maxWeight]: readonly [string, number, number],
  sex: string,
): WeightCategory {
  return {
    ...weightCategoryMetadata({}),
    id,
    name,
    minWeight,
    maxWeight,
    isJunior: false,
    sex,
  };
}

function buildLeagues(): LeagueData[] {
  return [{ id: 1, name: "Île-de-France" }];
}

/** Builds the competition organisation identity for preview frames. */
function buildOrganisation(): OrganisationData {
  return {
    id: FIXED_ORG_ID,
    name: "FFForce",
    code: "FFFORCE",
    countryCode: "FR",
    recordLabel: "Record de France",
    recordColor: "#002395",
    recordBadgeUrl: null,
    parentId: null,
  };
}

function buildRecords({
  ageCategories,
  discipline,
  organisation,
  templates,
  weightCategories,
}: {
  ageCategories: AgeCategory[];
  discipline: Discipline;
  organisation: OrganisationData;
  templates: AthleteData[];
  weightCategories: WeightCategory[];
}): RecordData[] {
  const templateRecords = templates.slice(0, 8).flatMap((athlete, index) => {
    const movement = MOVEMENTS[index % MOVEMENTS.length]!;
    const weight = Math.max(
      20,
      Number(athlete[`${movement}1` as AttemptField] ?? 0) - 2.5,
    );
    return [
      {
        id: index + 1,
        movement,
        weight,
        ageCategoryId: athlete.ageCategoryId,
        weightCategoryId: athlete.weightCategoryId,
        dateLocation: "Preview - 2026",
        athleteName: "Record Preview",
        disciplineId: discipline.id,
        sex: athlete.sex,
        sheetDate: "2026-08-13",
        organisationId: organisation.id,
        ageCategory: athlete.ageCategory,
        weightCategory: athlete.weightCategory,
        discipline,
        organisation,
      } as RecordData,
    ];
  });

  if (templateRecords.length > 0) return templateRecords;
  const ageCategory = ageCategories[2]!;
  return weightCategories.slice(0, 3).map(
    (weightCategory, index) =>
      ({
        id: index + 1,
        movement: MOVEMENTS[index % MOVEMENTS.length]!,
        weight: 100 + index * 25,
        ageCategoryId: ageCategory.id,
        weightCategoryId: weightCategory.id,
        dateLocation: "Preview - 2026",
        athleteName: "Record Preview",
        disciplineId: discipline.id,
        sex: weightCategory.sex,
        sheetDate: "2026-08-13",
        organisationId: organisation.id,
        ageCategory,
        weightCategory,
        discipline,
        organisation,
      }) as RecordData,
  );
}

function buildLevels({
  ageCategories,
  discipline,
  weightCategories,
}: {
  ageCategories: AgeCategory[];
  discipline: Discipline;
  weightCategories: WeightCategory[];
}): LevelData[] {
  const ageCategory = ageCategories[2]!;
  return weightCategories.slice(0, 4).map(
    (weightCategory, index) =>
      ({
        id: index + 1,
        minTotal: 300 + index * 50,
        maxTotal: 349.99 + index * 50,
        name: ["R3", "R2", "R1", "N2"][index] ?? "R3",
        ageCategoryId: ageCategory.id,
        weightCategoryId: weightCategory.id,
        disciplineId: discipline.id,
        sheetDate: "2026-08-13",
        ageCategory,
        weightCategory,
        discipline,
      }) as LevelData,
  );
}

function buildGroupSexes(
  sexProfile: CompetitionPreviewSexProfile,
  groupIndex: number,
): string[] {
  switch (sexProfile) {
    case "men":
      return Array(ATHLETES_PER_GROUP).fill("M");
    case "women":
      return Array(ATHLETES_PER_GROUP).fill("F");
    case "menWomen":
      return Array(ATHLETES_PER_GROUP).fill(groupIndex === 0 ? "M" : "F");
    case "mixedMen":
      return groupIndex === 0
        ? buildMixedSexes()
        : Array(ATHLETES_PER_GROUP).fill("M");
    case "mixedWomen":
      return groupIndex === 0
        ? buildMixedSexes()
        : Array(ATHLETES_PER_GROUP).fill("F");
    case "mixed":
    default:
      return buildMixedSexes();
  }
}

function buildMixedSexes(): string[] {
  return Array.from({ length: ATHLETES_PER_GROUP }, (_, index) =>
    index % 2 === 0 ? "M" : "F",
  );
}

function buildBirthDate(ageCategory: AgeCategory): Date {
  const age =
    ageCategory.ageMin +
    Math.floor((ageCategory.ageMax - ageCategory.ageMin) / 2);
  return new Date(2026 - age, 5, 15);
}

function buildRefereeLights(
  colors: string[],
  juryColor: string = "",
): RefereeLights[] {
  const [left = "", chief = "", right = ""] = colors;
  return [
    { type: "left", passed: Boolean(left), color: left, juryEnabled: false },
    {
      type: "chief",
      passed: Boolean(chief),
      color: chief,
      juryEnabled: false,
    },
    {
      type: "right",
      passed: Boolean(right),
      color: right,
      juryEnabled: false,
    },
    {
      type: "jury_1",
      passed: Boolean(juryColor),
      color: juryColor,
      juryEnabled: Boolean(juryColor),
    },
    { type: "jury_2", passed: false, color: "", juryEnabled: true },
    { type: "jury_3", passed: false, color: "", juryEnabled: true },
  ];
}

function decimal(value: number): PrismaRemote.Decimal {
  return new PrismaRemote.Decimal(value);
}

function roundToStep(value: number): number {
  return Math.round(value / 2.5) * 2.5;
}

function formatTimer(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}

const PREVIEW_FIRSTNAMES = [
  "Alex",
  "Sam",
  "Camille",
  "Morgan",
  "Charlie",
  "Lou",
  "Noa",
  "Max",
  "Nina",
  "Eli",
  "Robin",
  "Sasha",
  "Mila",
  "Romy",
  "Isa",
  "Jo",
];
